import { COURSE_ACCESS_LEVEL, isCourseFree } from "@repo/shared";

import type { CourseAccessLevel } from "@repo/shared";

/**
 * Los cuatro estados que /details tiene que saber servir en la MISMA url.
 *
 * Antes esta semántica vivía repartida en tres pantallas (la ficha pública, el detalle
 * interno y la presentación para inscriptos) y cada una decidía por su cuenta qué mostrar,
 * así que el mismo curso se veía distinto según por dónde entrabas.
 */
export const COURSE_DETAIL_STATE = {
  /** No lo tiene. Vista comercial: precio, comprar, carrito, inscripción directa. */
  VISITOR: "visitor",
  /** Tiene free preview de un curso pago: puede ver los capítulos freemium y mejorar. */
  PREVIEW: "preview",
  /** Acceso completo, todavía no arrancó. */
  READY: "ready",
  /** Acceso completo y ya arrancó (o terminó). */
  IN_PROGRESS: "in_progress",
} as const;

export type CourseDetailState = (typeof COURSE_DETAIL_STATE)[keyof typeof COURSE_DETAIL_STATE];

type CourseLike = {
  enrolled?: boolean;
  accessLevel?: CourseAccessLevel | null;
  purchased?: boolean;
};

type ProgressLike = { started: boolean };

export function resolveCourseDetailState(
  course: CourseLike | undefined | null,
  progress: ProgressLike,
): CourseDetailState {
  if (!course?.enrolled) return COURSE_DETAIL_STATE.VISITOR;

  // `accessLevel` es la fuente de verdad; `purchased` queda como respaldo por si el
  // endpoint todavía no lo trae (respuestas cacheadas de antes del deploy).
  const hasFullAccess = course.accessLevel
    ? course.accessLevel === COURSE_ACCESS_LEVEL.FULL
    : Boolean(course.purchased);

  if (!hasFullAccess) return COURSE_DETAIL_STATE.PREVIEW;

  return progress.started ? COURSE_DETAIL_STATE.IN_PROGRESS : COURSE_DETAIL_STATE.READY;
}

/**
 * ¿Se le puede ofrecer inscripción directa (sin carrito ni checkout)?
 *
 * Dos casos: el curso es gratis (se lleva el acceso completo), o es pago pero tiene
 * capítulos abiertos, en cuyo caso lo que se ofrece es la vista previa.
 */
export function resolveDirectEnrollOffer(course: {
  priceInCents?: number | null;
  mercadopagoProductId?: string | null;
  hasFreeChapter?: boolean | null;
  isPreinscription?: boolean | null;
}): { available: boolean; level: CourseAccessLevel | null } {
  // Una pre-inscripción es una reserva de algo que todavía no abrió: no hay contenido que
  // entregar, así que no se ofrece inscripción directa.
  if (course.isPreinscription) return { available: false, level: null };

  if (isCourseFree(course)) {
    return { available: true, level: COURSE_ACCESS_LEVEL.FULL };
  }

  if (course.hasFreeChapter) {
    return { available: true, level: COURSE_ACCESS_LEVEL.FREE_PREVIEW };
  }

  return { available: false, level: null };
}

/** La url del link de inscripción directa. Vive en la landing y su forma es contrato. */
export function buildDirectEnrollUrl(slug: string, level: CourseAccessLevel | null): string {
  const base = `/courses/${slug}/inscripcion`;
  return level === COURSE_ACCESS_LEVEL.FREE_PREVIEW ? `${base}?nivel=preview` : base;
}
